import { useContext, useMemo, useState } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';

import Utils from '../../../../utils/immersive_utils';
import { useThemeMode } from '../../../../providers/theme-provider';
import {
  dilettaPanelSx,
  DILETTA_TITLE,
  DILETTA_DIVIDER,
} from '../../../../components/v2/widgets/exotics-shared';
import DeribitIcon from '../../../../assets/icons/deribit-icon.svg';
import OKXIcon from '../../../../assets/icons/okx-icon.svg';
import CryptoIcon from '../../../../components/v2/crypto-icon';
import {
  IPortfolioPerformanceAccountReport,
  IPortfolioPerformanceAccounts,
  PortfolioPerformanceContext,
} from '../../../../providers/portfolio-performance';
import { isReferenceStrategyAccount } from '../../../../providers/portfolio-performance/reference-strategies/reference-strategy.constants';
import {
  computeDailyStats,
  MIN_RATIO_SAMPLE,
} from '../../../../providers/portfolio-performance/compute-daily-stats';
import {
  buildStatsHistorical,
  STATS_YEAR,
} from '../../../../providers/portfolio-performance/build-stats-historical';

interface PortfolioPerformanceOverviewTableProps {
  accounts: IPortfolioPerformanceAccounts[];
  reportsByAccount: Record<string, IPortfolioPerformanceAccountReport>;
  openAccountName: string | null;
  onSelectAccount: (accountName: string) => void;
  // Names of the rows ticked for the comparison chart below. Drives each
  // row's checkbox checked-state; membership is toggled via onToggleComparison.
  selectedForComparison: string[];
  onToggleComparison: (accountName: string) => void;
}

type SortKey = 'accountName' | 'pv' | 'daily' | 'mtd' | 'ytd' | 'maxDD' | 'sharpe' | 'sortino';

interface OverviewRow {
  accountName: string;
  exchange: string;
  symbol: string;
  isReference: boolean;
  denomination: string | null;
  pv: number | null;
  daily: number | null;
  mtd: number | null;
  ytd: number | null;
  sharpe: number | null;
  sortino: number | null;
  maxDD: number | null;
  // Daily-return sample size behind sharpe/sortino — drives the "Not enough
  // data" tooltip when they're suppressed on a freshly-opened account.
  ratioSample: number;
}

// Same 8-slot rhythm as the account card's own header (1 wide "identity"
// slot + evenly-spaced metric slots), so this table's columns line up
// visually with the card layout underneath it.
// Leading fixed-width slot holds the comparison checkbox; the identity +
// 7 metric slots are unchanged from the card header rhythm underneath.
const GRID_TEMPLATE = '32px 2.2fr repeat(7, 1fr)';

const formatCurrency = (value: number | null, denomination: string | null): string => {
  if (value === null) return '-';
  return denomination === 'USD' || denomination === 'USDC'
    ? Utils.valueToCurrency(value, 2)
    : Utils.formatNumber(value, 4);
};

const formatPct = (value: number | null, decimals: number): string => {
  if (value === null) return '-';
  const pct = value * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(decimals)}%`;
};

const formatRatio = (value: number | null): string => (value === null ? '-' : value.toFixed(2));

const pctColour = (value: number | null, isDark: boolean): string | undefined => {
  if (value === null) return undefined;
  return value >= 0 ? (isDark ? '#4ade80' : '#166534') : isDark ? '#f87171' : '#991b1b';
};

const compareNullable = (a: number | null, b: number | null): number => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
};

// Summary leaderboard sitting above the per-account cards — one row per
// account so a user can sort by Sharpe/Sortino/return instead of scrolling
// through every card. Deliberately built as a plain flex/grid list rather
// than the ag-grid TableGrid used for the transactions/risk tables — those
// are genuinely dense data dumps, but this sits right above the account
// card's own header and reads much better sharing its look (icon + bold
// name + small-caps metric labels) than a spreadsheet-style grid would.
// Row click opens that account's card below; the account-selection sidebar
// is untouched, this is purely an additional entry point on top of it.
export const PortfolioPerformanceOverviewTable = ({
  accounts,
  reportsByAccount,
  openAccountName,
  onSelectAccount,
  selectedForComparison,
  onToggleComparison,
}: PortfolioPerformanceOverviewTableProps) => {
  const { resolvedMode } = useThemeMode();
  const { isNamesBlurred } = useContext(PortfolioPerformanceContext);
  const isDark = resolvedMode === 'dark';
  const titleColour = isDark ? DILETTA_TITLE : '#092D3A';
  const labelColour = isDark ? 'rgba(194,229,255,0.5)' : 'rgba(9,45,58,0.5)';
  const subColour = isDark ? 'rgba(194, 229, 255, 0.55)' : 'rgba(9, 45, 58, 0.55)';

  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(key === 'accountName' ? 'asc' : 'desc');
  };

  const rows = useMemo<OverviewRow[]>(
    () =>
      accounts.map((account) => {
        const report = reportsByAccount[account.name];
        const isReference = isReferenceStrategyAccount(account.name);
        // Same buildStatsHistorical(...) + computeDailyStats(...) call the
        // account card itself uses for its Sharpe/Sortino/MaxDD chips (see
        // portfolio-performance-account-card) — identical inputs, identical
        // pure function, so this table's numbers can't drift from what you
        // see when you open that row.
        const dailyStats = computeDailyStats(buildStatsHistorical(report, isReference));
        return {
          accountName: account.name,
          exchange: account.exchange,
          symbol: account.symbols[0],
          isReference,
          denomination: report?.denomination ?? null,
          pv: report?.pvRaw ?? null,
          daily: report?.dailyReturnRaw ?? null,
          mtd: report?.mtdReturnRaw ?? null,
          ytd: report?.ytdReturnRaw ?? null,
          sharpe: dailyStats.sharpe,
          sortino: dailyStats.sortino,
          maxDD: dailyStats.maxDrawdownPct,
          ratioSample: dailyStats.sampleSize,
        };
      }),
    [accounts, reportsByAccount],
  );

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const cmp =
        sortKey === 'accountName'
          ? a.accountName.localeCompare(b.accountName)
          : compareNullable(a[sortKey], b[sortKey]);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir]);

  const HeaderCell = ({
    label,
    sortKeyFor,
    align = 'right',
  }: {
    label: string;
    sortKeyFor: SortKey;
    align?: 'left' | 'right';
  }) => (
    <Box
      onClick={() => toggleSort(sortKeyFor)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <Typography
        sx={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: labelColour }}
      >
        {label}
      </Typography>
      {sortKey === sortKeyFor && (
        <Typography sx={{ fontSize: 8, color: labelColour }}>
          {sortDir === 'asc' ? '▲' : '▼'}
        </Typography>
      )}
    </Box>
  );

  const MetricCell = ({
    value,
    color,
    title,
  }: {
    value: string;
    color?: string;
    title?: string;
  }) => (
    <Typography
      title={title}
      sx={{
        fontSize: 13,
        fontWeight: 600,
        textAlign: 'right',
        color: color ?? titleColour,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </Typography>
  );

  return (
    <Box sx={{ ...dilettaPanelSx, p: 2.5, mb: 2 }}>
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ color: titleColour, fontSize: 15, fontWeight: 700 }}>Overview</Typography>
        <Typography sx={{ color: subColour, fontSize: 12 }}>
          Click a column label to sort, click a row to open that account below
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: GRID_TEMPLATE, gap: 1, px: 0.5, pb: 1 }}>
        <Box />
        <HeaderCell label="Account" sortKeyFor="accountName" align="left" />
        <HeaderCell label="PV" sortKeyFor="pv" />
        <HeaderCell label="Daily" sortKeyFor="daily" />
        <HeaderCell label="MTD" sortKeyFor="mtd" />
        <HeaderCell label="YTD" sortKeyFor="ytd" />
        <HeaderCell label={`Max DD (${STATS_YEAR})`} sortKeyFor="maxDD" />
        <HeaderCell label={`Sharpe (${STATS_YEAR})`} sortKeyFor="sharpe" />
        <HeaderCell label={`Sortino (${STATS_YEAR})`} sortKeyFor="sortino" />
      </Box>

      <Box sx={{ borderTop: `1px solid ${DILETTA_DIVIDER}`, mb: 0.5 }} />

      {sortedRows.map((row) => (
        <Box
          key={row.accountName}
          onClick={() => onSelectAccount(row.accountName)}
          sx={{
            display: 'grid',
            gridTemplateColumns: GRID_TEMPLATE,
            alignItems: 'center',
            gap: 1,
            px: 0.5,
            py: 1,
            cursor: 'pointer',
            borderRadius: '8px',
            backgroundColor:
              row.accountName === openAccountName ? 'rgba(2, 106, 162, 0.16)' : 'transparent',
            '&:hover': {
              backgroundColor:
                row.accountName === openAccountName
                  ? 'rgba(2, 106, 162, 0.20)'
                  : 'rgba(2, 106, 162, 0.08)',
            },
            transition: 'background-color 120ms ease',
          }}
        >
          <Box
            // stopPropagation on both the wrapper and the checkbox keeps a
            // tick from bubbling to the row's onSelectAccount — ticking a row
            // for comparison must never also open its card below.
            onClick={(e) => e.stopPropagation()}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Checkbox
              size="small"
              checked={selectedForComparison.includes(row.accountName)}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onToggleComparison(row.accountName)}
              inputProps={{ 'aria-label': `Compare ${row.accountName}` }}
              sx={{ p: 0 }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {row.isReference ? (
              <Box sx={{ flexShrink: 0, display: 'flex' }}>
                <CryptoIcon symbol={row.symbol} size={18} />
              </Box>
            ) : (
              <Box
                component="img"
                src={row.exchange === 'deribit' ? DeribitIcon : OKXIcon}
                alt=""
                sx={{ width: 18, height: 18, flexShrink: 0 }}
              />
            )}
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: titleColour,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                // Reference strategy names (BTC/ETH/...) aren't sensitive -
                // only blur real traded-exchange accounts.
                filter: isNamesBlurred && !row.isReference ? 'blur(6px)' : 'none',
                transition: 'filter 150ms ease',
                userSelect: isNamesBlurred && !row.isReference ? 'none' : 'auto',
              }}
            >
              {row.accountName}
            </Typography>
          </Box>
          <MetricCell value={formatCurrency(row.pv, row.denomination)} />
          <MetricCell value={formatPct(row.daily, 2)} color={pctColour(row.daily, isDark)} />
          <MetricCell value={formatPct(row.mtd, 2)} color={pctColour(row.mtd, isDark)} />
          <MetricCell value={formatPct(row.ytd, 1)} color={pctColour(row.ytd, isDark)} />
          <MetricCell value={formatPct(row.maxDD, 1)} />
          <MetricCell
            value={formatRatio(row.sharpe)}
            title={
              row.ratioSample < MIN_RATIO_SAMPLE
                ? `Not enough data (${row.ratioSample}/${MIN_RATIO_SAMPLE} days)`
                : undefined
            }
          />
          <MetricCell
            value={formatRatio(row.sortino)}
            title={
              row.ratioSample < MIN_RATIO_SAMPLE
                ? `Not enough data (${row.ratioSample}/${MIN_RATIO_SAMPLE} days)`
                : undefined
            }
          />
        </Box>
      ))}
    </Box>
  );
};
